import React, { useEffect, useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { toast } from 'react-hot-toast'
import CheckIcon from '@mui/icons-material/Check'
import { Button, Modal } from '@/design-system'
import { ModernCreateModal } from '@/components'
import { useAddNoteTopic, useGetTopics, useUploadMedia } from '@/hooks/api/dashboard'
import { OddlyouNoteType, PrivacyType, TopicType } from '@/utils/constants'
import { Topic } from '@/types'
import { getPlainNoteTextFromNote } from '@/utils/helper'

export interface SaveToAlbumModalProps {
  open: boolean
  /** The captured screenshot, uploaded on submit. Null while the canvas is still rendering it. */
  file: File | null
  onClose: () => void
}

/** DS reskin of the legacy SaveScreenshotModal — attach the studio's screenshot to an album as a note. */
export function SaveToAlbumModal({ open, file, onClose }: SaveToAlbumModalProps) {
  const getTopics = useGetTopics()
  const uploadPhoto = useUploadMedia()
  const createTopicNote = useAddNoteTopic()

  const [selectedTopicID, setSelectedTopicID] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [showCreateDialog, setShowCreateDialog] = useState(false)

  const albums = useMemo(() => (getTopics.data || []) as Topic[], [getTopics.data])

  const loadAlbums = () => {
    getTopics.mutate({ pageNo: 1, interaction: Number(PrivacyType.PRIVATE), topicType: Number(TopicType.ALBUM) })
  }

  useEffect(() => {
    if (open) loadAlbums()
    else setSelectedTopicID(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file])
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    }
  }, [previewUrl])

  const submit = async () => {
    if (!file || !selectedTopicID) return
    setSaving(true)
    try {
      const fileInfo = await uploadPhoto.mutateAsync(file)
      const egoList = [
        {
          sequenceID: '1',
          egoID: fileInfo.egoID,
          photoAlbumURL: fileInfo.egoAlbumURL,
          photoFullURL: fileInfo.egoImageURL,
        },
      ]
      await createTopicNote.mutateAsync({
        noteName: 'Screenshot',
        topicID: selectedTopicID,
        noteText: JSON.stringify({ text: '<p>Screenshot</p>', attachments: [] }),
        egoItems: egoList,
        noteType: OddlyouNoteType.IMAGE.toString(),
      })
      toast.success('Screenshot saved to album', { duration: 2500 })
      onClose()
    } catch (err: any) {
      toast.error(err?.toString?.() || 'Failed to save screenshot')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Modal open={open} onClose={onClose} title='Save screenshot to an album'>
        {previewUrl && (
          <Box
            sx={{
              borderRadius: 'var(--ds-radius-md)',
              overflow: 'hidden',
              mb: 3,
              aspectRatio: '16 / 10',
              backgroundColor: 'var(--ds-ink)',
            }}
          >
            <Box
              component='img'
              src={previewUrl}
              alt=''
              sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          </Box>
        )}

        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--ds-ink)', mb: 1.5 }}>
          Choose an album
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            maxHeight: 220,
            overflowY: 'auto',
            mb: 2.5,
          }}
        >
          {getTopics.isLoading && (
            <Typography sx={{ fontSize: 13, color: 'var(--ds-ink-muted)', textAlign: 'center', py: 2 }}>
              Loading albums…
            </Typography>
          )}
          {!getTopics.isLoading && albums.length === 0 && (
            <Typography sx={{ fontSize: 13, color: 'var(--ds-ink-muted)', textAlign: 'center', py: 2 }}>
              No albums yet — create one below.
            </Typography>
          )}
          {albums.map((album) => {
            const selected = album.topicID === selectedTopicID
            return (
              <Box
                key={album.topicID}
                component='button'
                type='button'
                onClick={() => setSelectedTopicID(album.topicID)}
                sx={{
                  all: 'unset',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  padding: '10px 14px',
                  borderRadius: 'var(--ds-radius-md)',
                  backgroundColor: selected ? 'var(--ds-hover)' : 'transparent',
                  border: `1px solid ${selected ? 'var(--ds-ink)' : 'var(--ds-border)'}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: selected ? 600 : 500,
                    color: 'var(--ds-ink)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getPlainNoteTextFromNote(album.firstNote) || 'Untitled album'}
                </Typography>
                {selected && <CheckIcon sx={{ fontSize: 16, color: 'var(--ds-ink)', flexShrink: 0 }} />}
              </Box>
            )
          })}
        </Box>

        <Button variant='ghost' fullWidth onClick={() => setShowCreateDialog(true)} sx={{ mb: 2.5 }}>
          + Create new album
        </Button>

        <Box display='flex' justifyContent='flex-end' gap={1.5}>
          <Button variant='ghost' onClick={onClose}>
            Cancel
          </Button>
          <Button variant='primary' onClick={submit} disabled={!selectedTopicID || !file} loading={saving}>
            Save
          </Button>
        </Box>
      </Modal>

      <ModernCreateModal
        open={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false)
          loadAlbums()
        }}
      />
    </>
  )
}
